import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, DatePicker, Radio, RadioGroup, TextInput, Textarea } from '@opendate-app/setlist';

const hintStyle = { color: "#686868", fontSize: "13px" };

const toIso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const fromIso = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const parseTypedDate = (text) => {
  const s = String(text || '').trim();
  let parts = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  let year, month, day;
  if (parts) [, month, day, year] = parts.map(Number);
  else if ((parts = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) [, year, month, day] = parts.map(Number);
  else if ((parts = s.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/))) {
    month = MONTHS.indexOf(parts[1].slice(0, 3).toLowerCase()) + 1;
    day = Number(parts[2]);
    year = Number(parts[3]);
  }
  if (!month || !day || !year) return null;
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

const GiftRecipientForm = ({ gift, onChange, interval, emailError = false, keepPaying = false }) => {
  const handle = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    onChange({ ...gift, [field]: value });
  };
  const firstName = gift.name.trim().split(/\s+/)[0];
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const oneYearOut = new Date(new Date(today).setFullYear(today.getFullYear() + 1));
  const [dateError, setDateError] = useState(false);
  const parseSendOn = (text) => {
    const date = parseTypedDate(text);
    const usable = date && date >= today && date <= oneYearOut;
    if (!usable && String(text || '').trim() !== '') {
      setDateError(true);
      if (gift.send_on) onChange({ ...gift, send_later: true, send_on: '' });
    }
    return date;
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <div className="mb-3">
        <Checkbox
          id="membership-buy-as-gift"
          checked={gift.enabled}
          onChange={handle('enabled')}
          label={<><strong>This is a gift</strong><div style={hintStyle}>The membership goes to someone else. You pay.</div></>}
        />
      </div>

      {gift.enabled && (
        <div>
          <p className="small" style={{ color: "#686868" }}>
            This gift covers one {interval === 'month' ? 'month' : 'year'}.{!keepPaying && " You pay once and it won't renew."}
          </p>
          <div className="form-row">
            <div className="col-12 col-sm-6 mb-3">
              <TextInput
                id="membership-gift-name"
                label="Recipient name"
                value={gift.name}
                onChange={handle('name')}
                error={(gift.email || gift.name) && !gift.name.trim() ? 'Enter their name.' : undefined}
              />
            </div>
            <div className="col-12 col-sm-6 mb-3">
              <TextInput
                id="membership-gift-email"
                type="email"
                label="Recipient email"
                value={gift.email}
                onChange={handle('email')}
                error={emailError ? 'Enter a valid email.' : undefined}
                hint="Their membership and login use this email."
              />
            </div>
          </div>

          <div className="mb-3">
            <Checkbox
              id="membership-gift-anonymous"
              checked={gift.anonymous}
              onChange={handle('anonymous')}
              label={<><strong>Keep my name private</strong><div style={hintStyle}>{firstName || 'They'} won't see who sent it. The venue still will, for receipts and refunds.</div></>}
            />
          </div>

          <div className="mb-3">
            <Textarea
              id="membership-gift-note"
              label="Message (optional)"
              rows={2}
              maxLength={1000}
              value={gift.note}
              onChange={handle('note')}
            />
            {gift.anonymous && (
              <div className="mt-1" style={hintStyle}>
                Your message is shown exactly as written, so leave out anything that gives you away.
              </div>
            )}
          </div>

          <div className="mb-3">
            <div className="mb-1" style={{ fontSize: "14px" }}><strong>Send the gift email</strong></div>
            <div className="d-flex align-items-center flex-wrap">
              <RadioGroup name="membership-gift-send" value={gift.send_later ? 'later' : 'now'} onChange={(value) => { if (value === 'now') setDateError(false); onChange({ ...gift, send_later: value === 'later' }); }}>
                <span className="mr-3"><Radio id="membership-gift-send-now" value="now" label="Now" /></span>
                <span className="mr-3"><Radio id="membership-gift-send-later" value="later" label="On a date" /></span>
              </RadioGroup>
              <div style={{ width: "200px" }}>
                <DatePicker
                  id="membership-gift-send-on"
                  placeholder="Nov 13, 2026"
                  value={gift.send_on ? fromIso(gift.send_on) : null}
                  min={today}
                  max={oneYearOut}
                  parseDate={parseSendOn}
                  error={dateError ? 'Pick a date from today to a year out.' : undefined}
                  onBlur={(e) => { if (e.target.value.trim() === '') setDateError(false); }}
                  onChange={(date) => { setDateError(false); onChange({ ...gift, send_later: true, send_on: date ? toIso(date) : '' }); }}
                />
              </div>
            </div>
            <div className="mt-1" style={hintStyle}>
              Their membership starts when they claim it, so an unopened email doesn't use up their term.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

GiftRecipientForm.propTypes = {
  gift: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    anonymous: PropTypes.bool.isRequired,
    note: PropTypes.string.isRequired,
    send_later: PropTypes.bool.isRequired,
    send_on: PropTypes.string.isRequired
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  interval: PropTypes.oneOf(['month', 'year']).isRequired,
  emailError: PropTypes.bool,
  keepPaying: PropTypes.bool
};

GiftRecipientForm.defaultProps = {
  emailError: false,
  keepPaying: false
};

export default GiftRecipientForm;
